import { assertNotNullOrUndefined } from '../../utils/assert/assertNotNullOrUndefined';
import { KeyValueStorageStrategy, Item } from './KeyValueStorageStrategy';
export class IndexedDbKeyValueStorageStrategy extends KeyValueStorageStrategy {
  private database: IDBDatabase | null = null;
  public async initStore(storeName: string, tableNames: Array<string>): Promise<void> {
    const preUpgradeDatabase = await this.openDatabase(storeName);
    const tableNamesToCreate = tableNames.filter(tableName => !preUpgradeDatabase.objectStoreNames.contains(tableName));
    if (tableNamesToCreate.length) {
      this.database = await this.createTables(preUpgradeDatabase, tableNamesToCreate);
    } else {
      this.database = preUpgradeDatabase;
    }
  }
  public async getItem<T>(key: string, tableName: string): Promise<T | undefined> {
    return this.executeDatabaseOperation(tableName, 'readonly', objectStore => objectStore.get(key));
  }
  public async setItem(key: string, value: unknown, tableName: string): Promise<void> {
    await this.executeDatabaseOperation(tableName, 'readwrite', objectStore => objectStore.put(value, key));
  }
  public async removeItem(key: string, tableName: string): Promise<void> {
    await this.executeDatabaseOperation(tableName, 'readwrite', objectStore => objectStore.delete(key));
  }
  public async getItems<T = unknown>(tableName: string): Promise<Array<T>> {
    return this.executeDatabaseOperation(tableName, 'readonly', objectStore => objectStore.getAll());
  }
  public async setItems<T = unknown>(items: Array<Item<T>>, tableName: string): Promise<void> {
    await this.executeBulkDatabaseOperation(tableName, 'readwrite', objectStore => items.map(item => objectStore.put(item.value, item.key)));
  }
  public async removeItems(keys: Array<string>, tableName: string): Promise<void> {
    await this.executeBulkDatabaseOperation(tableName, 'readwrite', objectStore => keys.map(key => objectStore.delete(key)));
  }
  public async clear(tableName: string): Promise<void> {
    await this.executeDatabaseOperation(tableName, 'readwrite', objectStore => objectStore.clear());
  }

  /**
   * @param preUpgradeDatabase - database will get closed, be careful
   * @param tableNames
   * @returns a newly opened database with the new tableNames
   */
  private async createTables(preUpgradeDatabase: IDBDatabase, tableNames: Array<string>): Promise<IDBDatabase> {
    const oldVersion = preUpgradeDatabase.version;
    const newVersion = oldVersion + 1;
    preUpgradeDatabase.close();
    return this.openDatabase(preUpgradeDatabase.name, {
      version: newVersion,
      onUpgradeNeeded: database => {
        tableNames.forEach(tableName => {
          try {
            database.createObjectStore(tableName);
          } catch (error) {
            if (error instanceof Error && error.name === 'ConstraintError') {
              throw new Error(`The database ${preUpgradeDatabase.name} has been upgraded from version ${oldVersion} to version ${newVersion} but the storage ${tableName} already exists.`);
            } else {
              throw error;
            }
          }
        });
      }
    });
  }
  private async openDatabase(storeName: string, options?: OpenDatabaseOptions): Promise<IDBDatabase> {
    return new Promise((res, rej) => {
      const openHandler = indexedDB.open(storeName, options?.version);
      openHandler.onsuccess = () => {
        res(openHandler.result);
      };
      openHandler.onerror = () => {
        rej(new Error('failed to open a indexedDB database'));
      };
      openHandler.onupgradeneeded = () => {
        options?.onUpgradeNeeded?.(openHandler.result);
      };
    });
  }
  private executeDatabaseOperation<T>(tableName: string, mode: IDBTransactionMode, createRequest: (objectStore: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    const {
      transaction,
      objectStore
    } = this.prepareDatabaseOperation(tableName, mode);
    const request = createRequest(objectStore);
    return new Promise((res, rej) => {
      request.onsuccess = () => {
        res(request.result);
      };
      request.onerror = () => {
        rej(request.error);
      };
      transaction.onerror = () => {
        rej(transaction.error);
      };
      transaction.onabort = () => {
        rej(transaction.error);
      };
    });
  }
  private executeBulkDatabaseOperation(tableName: string, mode: IDBTransactionMode, createRequests: (objectStore: IDBObjectStore) => Array<IDBRequest>): Promise<void> {
    const {
      transaction,
      objectStore
    } = this.prepareDatabaseOperation(tableName, mode);
    createRequests(objectStore);
    return new Promise((res, rej) => {
      transaction.oncomplete = () => {
        res();
      };
      transaction.onerror = () => {
        rej(transaction.error);
      };
      transaction.onabort = () => {
        rej(transaction.error);
      };
    });
  }
  private prepareDatabaseOperation(tableName: string, mode: IDBTransactionMode): {
    transaction: IDBTransaction;
    objectStore: IDBObjectStore;
  } {
    const database = this.getDatabase();
    const transaction = database.transaction(tableName, mode);
    const objectStore = transaction.objectStore(tableName);
    return {
      transaction,
      objectStore
    };
  }
  private getDatabase(): IDBDatabase {
    assertNotNullOrUndefined(this.database, 'IndexedDbKeyValueStorageStrategy is not initialized');
    return this.database;
  }
}
type OpenDatabaseOptions = {
  onUpgradeNeeded?: (database: IDBDatabase) => void;
  version?: number;
};